import { useSyncExternalStore } from "react";

/** Tiny observable value store with React binding. */
export class Observable<T> {
  private listeners = new Set<() => void>();
  constructor(private value: T) {}
  get(): T {
    return this.value;
  }
  set(next: T | ((prev: T) => T)): void {
    const v = typeof next === "function" ? (next as (p: T) => T)(this.value) : next;
    if (Object.is(v, this.value)) return;
    this.value = v;
    for (const l of this.listeners) l();
  }
  patch(partial: Partial<T>): void {
    this.set({ ...(this.value as object), ...(partial as object) } as T);
  }
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }
}

export function useObservable<T, S = T>(obs: Observable<T>, selector?: (v: T) => S): S {
  const sel = selector ?? ((v: T) => v as unknown as S);
  return useSyncExternalStore(
    (cb) => obs.subscribe(cb),
    () => sel(obs.get()),
    () => sel(obs.get())
  );
}
